import { useEffect, useRef, useState } from "react";
import { Search, RefreshCw, Loader2, CheckCircle2, FileText, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import { toast } from "sonner";
import { errorDetailMessage } from "@/lib/errorDetailMessage";

/**
 * Search Monday.com deals in the "Ready for Doc's" group and pre-populate
 * the UK contract wizard. Shown at the top of Step 1.
 *
 * Props
 *   onSelect(deal)   — invoked with the normalised deal payload from
 *                      GET /contracts/monday/ready-for-docs.
 */
export default function MondayDealSearch({ onSelect }) {
  const [query, setQuery] = useState("");
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [open, setOpen] = useState(false);
  const [lastSelected, setLastSelected] = useState(null);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  // Close on Escape + on outside-click so the operator can dismiss the
  // dropdown and crack on with manual entry.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onClickAway = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClickAway);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClickAway);
    };
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        setLoading(true);
        const { data } = await api.get("/contracts/monday/ready-for-docs", {
          params: { search: query || undefined, limit: 25 },
        });
        setDeals(data?.deals || []);
      } catch (err) {
        console.error("Monday search failed", err);
        setDeals([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query]);

  const refresh = async () => {
    try {
      setRefreshing(true);
      const { data } = await api.post("/contracts/monday/refresh-ready-for-docs");
      toast.success(
        `Synced from Monday: ${data?.created || 0} new, ${data?.updated || 0} updated`
      );
      // Re-trigger the search with current query
      const res = await api.get("/contracts/monday/ready-for-docs", {
        params: { search: query || undefined, limit: 25 },
      });
      setDeals(res.data?.deals || []);
      // Re-open the dropdown so the user can see the freshly synced rows
      setOpen(true);
    } catch (err) {
      toast.error("Failed to refresh from Monday: " + errorDetailMessage(err, err.message));
    } finally {
      setRefreshing(false);
    }
  };

  const handleSelect = (deal) => {
    setLastSelected(deal);
    setOpen(false);
    setQuery(`${deal.sf_number} · ${deal.company_name}`);
    onSelect?.(deal);
    toast.success(`Pre-filled from ${deal.sf_number}`);
  };

  return (
    <div className="relative" data-testid="monday-deal-search" ref={containerRef}>
      <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-3 space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-semibold text-blue-900">
            <FileText className="w-4 h-4" />
            Pull from Monday — Ready for Doc's
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={refresh}
              disabled={refreshing}
              className="h-7 px-2 text-xs"
              data-testid="monday-refresh-btn"
            >
              {refreshing ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />}
              Refresh
            </Button>
            {open && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setOpen(false)}
                className="h-7 px-2 text-xs text-slate-600"
                data-testid="monday-close-dropdown"
                title="Hide list and enter details manually (Esc)"
              >
                <X className="w-3 h-3 mr-1" />
                Close
              </Button>
            )}
          </div>
        </div>

        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
              setLastSelected(null);
            }}
            onFocus={() => setOpen(true)}
            placeholder="Search by SF number or company name…"
            className="pl-8 bg-white"
            data-testid="monday-deal-search-input"
          />
          {loading && (
            <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 animate-spin" />
          )}
        </div>

        {open && deals.length > 0 && !lastSelected && (
          <div
            className="absolute left-3 right-3 z-30 mt-1 max-h-80 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg"
            data-testid="monday-deal-results"
          >
            {deals.map((d) => (
              <button
                type="button"
                key={d.monday_item_id}
                onClick={() => handleSelect(d)}
                className="w-full text-left p-3 hover:bg-slate-50 border-b last:border-b-0 transition-colors"
                data-testid={`monday-deal-row-${d.sf_number}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono font-semibold text-slate-700">
                        {d.sf_number || "—"}
                      </span>
                      <span className="font-medium text-sm truncate">{d.company_name}</span>
                      {d.offer_form_id && (
                        <Badge variant="outline" className="text-[10px] bg-emerald-50 border-emerald-300 text-emerald-700">
                          Form ✓
                        </Badge>
                      )}
                      {d.offer_pdf_url && (
                        <Badge variant="outline" className="text-[10px] bg-blue-50 border-blue-300 text-blue-700">
                          PDF ✓
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-3">
                      <span>£{d.loan_amount.toLocaleString()}</span>
                      <span>·</span>
                      <span>FR {d.factor_rate}</span>
                      <span>·</span>
                      <span>{d.loan_term_weeks}w</span>
                      <span>·</span>
                      <span className="capitalize">{d.payment_frequency}</span>
                      {d.admin_fee > 0 && (<><span>·</span><span>Fee £{d.admin_fee.toLocaleString()}</span></>)}
                    </div>
                  </div>
                  <span className="text-[11px] text-slate-400 shrink-0">{d.status}</span>
                </div>
              </button>
            ))}
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="w-full text-left p-2 text-xs text-slate-500 hover:bg-slate-50 border-t bg-slate-50/50"
              data-testid="monday-skip-manual"
            >
              Skip — enter details manually  <span className="text-[10px] text-slate-400 ml-1">(Esc)</span>
            </button>
          </div>
        )}

        {lastSelected && (
          <div className="flex items-start gap-2 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded p-2">
            <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <div>
              Pre-filled from <span className="font-semibold">{lastSelected.sf_number}</span>: loan amount, term, factor, frequency, fees, contact name & email.
              {lastSelected.offer_form_id && (
                <span className="block mt-0.5">
                  Offer Form ID <span className="font-mono">{lastSelected.offer_form_id}</span> captured.
                </span>
              )}
              {lastSelected.offer_pdf_url && (
                <span className="block mt-0.5">
                  Offer PDF proposal will be attached to the envelope.
                </span>
              )}
              <button
                type="button"
                onClick={() => { setLastSelected(null); setQuery(""); setOpen(false); }}
                className="text-blue-700 hover:underline ml-1"
                data-testid="monday-deal-clear"
              >
                Clear
              </button>
            </div>
          </div>
        )}

        {open && deals.length === 0 && !loading && query && (
          <div className="text-xs text-slate-500 px-1">
            No matching deals in "Ready for Doc's". Try a different search, or hit Refresh to pull latest from Monday.
          </div>
        )}
      </div>
    </div>
  );
}
